import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  HeaderComponent,
  TableComponent,
  BodyComponent,
  ActionComponent,
  ButtonComponent,
  StatusTag,
  CountryFlag,
  ErrorComponent,
  CreateButton,
} from "@comps/components";
import { statusClass, statusLabel } from "@comps/data/StatusTag";
import { makeRequest } from "@utils/helpers";
import { getUserCasesReq } from "./requests";
import moment from "moment";
import dayjs from "dayjs";
import { Icon } from "@iconify/react";
import { Tooltip, Input } from "antd";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import SubmitStlcCase from "./components/SubmitStlcCase";
import CreateCase from "@mods/caseManagement/cases/components/CreateCase";
import { deleteCaseReq } from "@mods/caseManagement/cases/requests";
import { isDoctorRole, checkPermission } from "@comps/commonFunctions";
import toast from "@comps/toast";
import "./caseList.css";

/**
 * Every case list in the app.
 *
 * The six case pages were near-identical copies of ~240 lines each; the
 * only real differences were the `type` sent to the API, which columns
 * appear, and whether a row can be edited. Those are now the config
 * object each route passes in — see caseListConfig.js.
 */
const CaseListPage = ({ config }) => {
  const navigate = useNavigate();
  const authUser = useSelector((state) => state.auth.authUser);

  const [dataSource, setDataSource] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loader, setLoader] = useState(false);
  const [failed, setFailed] = useState(false);
  const [childComponent, setChildComponent] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} cases`,
  });

  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  const onSuccess = (data) => {
    setFailed(false);
    setDataSource(data.data || []);
    setPagination((current) => ({
      ...current,
      current: data.current_page,
      pageSize: data.per_page,
    }));
    setTotalRecords(data.total);
  };

  const onError = () => setFailed(true);

  // The dashboard's Overdue tile links here with ?overdue=1, and the
  // toolbar toggle below writes the same parameter — so the filter is in
  // the URL either way, and survives a reload, a share and the back
  // button. Admin-only, and the server enforces that: this only decides
  // what to ask for.
  const [searchParams, setSearchParams] = useSearchParams();
  const overdueOnly = searchParams.get('overdue') === '1';

  const toggleOverdue = () => {
    const next = new URLSearchParams(searchParams);
    if (overdueOnly) {
      next.delete('overdue');
    } else {
      next.set('overdue', '1');
    }
    setSearchParams(next);
  };

  const getCasesData = useCallback((overrides = {}) => {
    const payload = {
      type: config.type,
      page: paginationRef.current.current || 1,
      limit: paginationRef.current.pageSize || 10,
      search: searchText,
      overdue: overdueOnly,
      ...overrides,
    };
    makeRequest(setLoader, getUserCasesReq, payload, onSuccess, onError);
  }, [config.type, searchText, overdueOnly]);

  useEffect(() => {
    // Back to page 1, but keeping whatever is in the search box: this
    // also runs when the Overdue filter is toggled, and clearing the
    // search there would leave the box full of text the list ignored.
    getCasesData({ page: 1 });
  }, [config.type, overdueOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Debounced so typing a case ID doesn't fire a request per keystroke.
   * Skipped on mount: the effect above has already asked for page 1 with
   * an empty search, and running this too made every page load fetch the
   * same list twice, the second answer overwriting the first.
   */
  const searchHasChanged = useRef(false);

  useEffect(() => {
    if (!searchHasChanged.current) {
      searchHasChanged.current = true;
      return;
    }
    const timer = setTimeout(() => {
      setPagination((current) => ({ ...current, current: 1 }));
      getCasesData({ search: searchText, page: 1 });
    }, 350);
    return () => clearTimeout(timer);
  }, [searchText]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTableChange = (newPagination) => {
    const updated = {
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    };
    setPagination(updated);
    getCasesData({ page: updated.current, limit: updated.pageSize });
  };

  const submitStlcFile = (record) => {
    setChildComponent(
      <SubmitStlcCase
        caseid={ record.id }
        handleCancel={ () => setChildComponent(null) }
        // The modal calls onSubmitted, not onSuccess — the row was left
        // showing "Submit STL file" over a case that had just had one.
        onSubmitted={ () => { setChildComponent(null); getCasesData(); } }
      />
    );
  };

  const onEdit = config.onEditComponent
    ? (record) => {
      const prepared = {
        // antd 5's DatePicker is built on dayjs, not moment — the form
        // has to convert this back anyway, so hand it the type it wants.
        ...record,
        dob: record.dob ? dayjs(record.dob) : record.dob,
        case_type: Number(record.case_type),
      };
      setChildComponent(
        config.onEditComponent({
          data: prepared,
          // Refetch rather than merging the response in place: the update
          // reply doesn't carry relation-derived fields, and this case may
          // have just changed status server side.
          onUpdated: () => { setChildComponent(null); getCasesData(); },
          onClosed: () => setChildComponent(null),
        })
      );
    }
    : null;

  const onCreateCase = () => {
    setChildComponent(
      <CreateCase
        onCreated={ () => { setChildComponent(null); getCasesData(); } }
        onClosed={ () => setChildComponent(null) }
      />
    );
  };

  // Deleting a case on a doctor's behalf is an admin power, not a
  // permission any role is granted — Case Management's own Cases table
  // (which used to offer this) is gone, so it lives here now.
  const onDeleteCase = (record) => {
    makeRequest(setLoader, deleteCaseReq, record.id, () => {
      toast.success("Case deleted successfully");
      getCasesData();
    }, Function);
  };

  const showOwnership = config.showOwnership !== false;
  // is_admin (from login) is user.is_system OR role.is_system. role_id === 1
  // named one seeded role and missed the others — a Super Admin on
  // role_id 2 was shown the doctor's view of this table.
  const isAdmin = !!authUser?.is_admin;
  const canCreateCase = config.showCreateButton
    && (isAdmin || isDoctorRole())
    && checkPermission(config.module + "_create");

  const columns = [
    {
      title: "Patient",
      key: "patient",
      // No thumbnail here on purpose: a patient photo is clinical data,
      // it is often missing, and at 38px it tells a reader nothing the
      // name does not. The full set is on the case detail page.
      render: (record) => (
        <span className="case-patient-meta">
          <span className="case-patient-name">{ record.name || "Unknown" }</span>
        </span>
      ),
    },

    // The doctor is whoever the case is assigned to, not whoever typed it
    // in — an admin raising a case for a doctor used to show up here as
    // the doctor. Admin-only: a doctor's list is their own cases, and who
    // a case belongs to in another clinic is not a technician's to read.
    // belongs_to_id is a recent, nullable column, so cases created before
    // it existed fall back to their creator, which is what the API treats
    // an unset owner as.
    ...(showOwnership && isAdmin
      ? [{
        title: "Doctor",
        key: "doctor",
        render: (record) => record.belongs_to_user?.name || record.created_by_user?.name || "--",
      }]
      : []),

    // Where the case came from, i.e. the country of the doctor it belongs
    // to. Admin-only: everyone else is already looking at a single
    // country's worth of cases, so the column would say the same thing on
    // every row.
    ...(isAdmin
      ? [{
        title: "Country",
        key: "country",
        render: (record) => <CountryFlag code={ record.country } fallback="Not set" />,
      }]
      : []),

    // Single arch or both arches — shown to everyone, as the old portal did.
    {
      title: "Case Type",
      key: "case_type",
      render: (record) => record.type?.name || "--",
    },

    {
      title: "Updated",
      key: "when",
      // Which timestamp a list goes by is the list's call: Approved
      // counts from the approval, not from the day the case was raised.
      render: (record) => {
        const when = record[config.timeField || "created_at"];
        return when
          ? <Tooltip title={ moment(when).format("DD MMM YYYY, HH:mm") }>
            <span className="case-when">{ moment(when).fromNow() }</span>
          </Tooltip>
          : "—";
      },
    },

    {
      title: "Status",
      key: "status",
      render: (record) => (
        <div className="case-status-cell">
          { config.onHoldFlag && record.refine_id === record.id
            ? <span className="status-tag status-modification">On hold</span>
            : <StatusTag
              status={ record.status }
              label={ record.status === 1 && record.taken_by_user?.name
                ? `Taken by ${record.taken_by_user.name}`
                : statusLabel(record.status) }
            /> }

          {/* Overdue: nobody has taken this case in 24 hours. Not a
              status — the case is still plainly Pending — and admin-only,
              which needs no check here: the server does not send the
              field to a doctor or a technician at all. */}
          { record.is_overdue &&
            <span className="status-tag status-overdue">
              <Icon icon="tabler:clock-exclamation" width={ 13 } /> Overdue
            </span>
          }

          {/* An approved case is waiting on its STL, and cannot be
              completed without one — offered from the moment it is
              approved rather than only once an admin has asked for the
              file, which left a doctor who had one nowhere to put it. */}
          { record.status === 4 && !record.file_link &&
            <button type="button" className="case-inline-action" onClick={ () => submitStlcFile(record) }>
              <Icon icon="tabler:link" width={ 14 } /> Submit STL file
            </button> }
        </div>
      ),
    },

    {
      title: "Case ID",
      dataIndex: "case_number",
      key: "case_number",
      width: 140,
      render: (value) => <span className="mono">{ value || "—" }</span>,
    },

    {
      title: "Actions",
      key: "actions",
      align: "right",
      width: 120,
      render: (record) => (
        <ActionComponent
          module={ config.module }
          each={ record }
          onEdit={ onEdit }
          // Only an admin ever sees the delete action here, regardless of
          // what the "cases" module permission grants everyone else.
          onDelete={ isAdmin ? onDeleteCase : undefined }
          deleteConfirm={ (each) => ({
            title: "Delete this case?",
            description: `Case ${each.case_number} and everything attached to it — photos, x-rays, scans and its treatment plan — will be deleted.`,
          }) }
        >
          <Tooltip title="View details">
            <Link className="row-action" to={ `/cases/details/${record.id}` } aria-label="View details">
              <Icon icon="tabler:eye" width={ 18 } />
            </Link>
          </Tooltip>
        </ActionComponent>
      ),
    },
  ];

  return (
    <>
      { childComponent }

      <HeaderComponent headers={ config.headers } description={ config.description }>
        {/* New case only exists on lists that opt into it (New Cases),
            and even there only for a Super Admin or a doctor — not a
            technician or any other role, whatever the "cases" permission
            grants them. It sits in the page header, above the search. */}
        { canCreateCase &&
          <CreateButton text="New case" onClick={ onCreateCase } /> }
      </HeaderComponent>

      <BodyComponent
        flush
        module={ config.module }
        toolbar={
          <div className="case-toolbar">
            <ButtonComponent
              type="default"
              icon={ <Icon icon="tabler:refresh" width={ 17 } /> }
              onClick={ () => getCasesData() }
            >
              Refresh
            </ButtonComponent>

            { config.showMineLink &&
              <ButtonComponent
                type="default"
                icon={ <Icon icon="tabler:user-check" width={ 17 } /> }
                onClick={ () => navigate("/cases/mine") }
              >
                Your cases
              </ButtonComponent> }

            {/* Overdue is nobody's status — it is the queue not moving,
                and only an admin is shown it at all. The badge already
                marks the rows; this is how you get to them without
                paging through everything looking for one. */}
            { config.showOverdueFilter && isAdmin &&
              <ButtonComponent
                type={ overdueOnly ? "primary" : "default" }
                danger={ overdueOnly }
                icon={ <Icon icon="tabler:clock-exclamation" width={ 17 } /> }
                onClick={ toggleOverdue }
              >
                { overdueOnly ? "Showing overdue only" : "Overdue" }
              </ButtonComponent> }
          </div>
        }
        extra={
          <Input
            className="case-search"
            placeholder="Search by case ID or patient"
            allowClear
            value={ searchText }
            onChange={ (e) => setSearchText(e.target.value) }
            prefix={ <Icon icon="tabler:search" width={ 17 } style={ { color: 'var(--ink-faint)' } } /> }
          />
        }
      >
        { failed
          ? <ErrorComponent
            title="These cases didn't load"
            description="The server didn't answer. Check your connection, then try again."
            onRetry={ () => getCasesData() }
          />
          : <TableComponent
            loader={ loader }
            columns={ columns }
            dataSource={ dataSource }
            spineOf={ (record) =>
              config.onHoldFlag && record.refine_id === record.id
                ? "status-modification"
                : statusClass(record.status) }
            emptyState={
              searchText
                ? {
                  icon: "tabler:search-off",
                  title: `No cases match “${searchText}”`,
                  description: "Check the case ID, or clear the search to see the whole list.",
                }
                : overdueOnly
                  // Otherwise a filtered-empty list said "No cases yet"
                  // over a system full of cases.
                  ? {
                    icon: "tabler:clock-check",
                    title: "Nothing is overdue",
                    description: "Every case has been picked up within 24 hours. Turn off the Overdue filter to see the whole list.",
                  }
                  : config.emptyState
            }
            pagination={ { ...pagination, total: totalRecords } }
            onChange={ handleTableChange }
          /> }
      </BodyComponent>
    </>
  );
};

export default CaseListPage;
